/**
 * Where Flight data QC's heights come from (docs/specs/geosoft-qc.md §17):
 *
 * - **R13**: a survey block's planned terrain clearance pre-filled from the project's job safety analysis (JSA).
 *   Teslyn's own JSA of the project comes first (its PDF, as made and signed: Teslyn's JSA prints each block's survey
 *   height), then the other JSA PDFs in the project's `Documents` folder (TJR's, `<job>_<aircraft>_JSA.pdf`). Teslyn
 *   Field's `jsa_heights` reads the PDFs (`teslyn-core::jsa_pdf`); this module matches their blocks to the project's
 *   and says where a value came from. The person can always change it; nothing is saved until they save.
 * - **F11**: the project's survey height, lower working height and hard deck given to the aircraft type's own Geosoft
 *   heights script (script 2 or 2.1, `DTM_plus_Survey_HT = DTM + 35` and so on), in the review job's copy of the
 *   script, never in the client's own file (`teslyn-geosoft::review::with_heights`).
 */
import type { Block } from './model.js';

// ------------------------------------------------------------------------------------------------ R13: the JSA

/** One JSA PDF's survey heights (`jsa_heights` in Teslyn Field, `JsaBlockHeight` in `teslyn-core::jsa_pdf`). */
export interface JsaHeightsSource {
  /** `teslyn_jsa`: the PDF of Teslyn's JSA record of the project; `jsa_pdf`: another JSA PDF (TJR's). */
  readonly kind: 'teslyn_jsa' | 'jsa_pdf';
  /** The file name (`F26048_Benz_Mining_Corp_C210_JSA.pdf`). */
  readonly file: string;
  /** The folder under `Documents` it is in (`Job Specific Documents`). */
  readonly folder: string;
  /** Teslyn's JSA: its revision; null for another PDF. */
  readonly revision: number | null;
  readonly blocks: readonly { readonly block_name: string; readonly survey_height_m: number }[];
}

/** A block's clearance from a JSA, and which. */
export interface JsaClearance {
  readonly block_id: string;
  readonly metres: number;
  /** The block's name as the JSA prints it. */
  readonly jsa_block: string;
  readonly source: JsaHeightsSource;
}

/** A block name compared loosely: case, spaces, underscores and punctuation do not count (`Mt Morgan _ Fixed Wing`). */
const nameKey = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Each survey block's clearance from the first source that gives it (the sources in order: Teslyn's JSA first). A
 * block is matched by name; when a JSA has one block and the project one survey block, they are the same block
 * whatever their names (TJR's block names were typed separately from Teslyn's).
 */
export function jsaClearances(
  blocks: readonly Pick<Block, 'id' | 'name' | 'kind'>[],
  sources: readonly JsaHeightsSource[],
): JsaClearance[] {
  const survey = blocks.filter((b) => b.kind === 'survey');
  const out: JsaClearance[] = [];
  for (const b of survey) {
    for (const src of sources) {
      const byName = src.blocks.find((x) => nameKey(x.block_name) === nameKey(b.name));
      const only = !byName && src.blocks.length === 1 && survey.length === 1 ? src.blocks[0] : undefined;
      const hit = byName ?? only;
      if (hit && hit.survey_height_m > 0) {
        out.push({ block_id: b.id, metres: hit.survey_height_m, jsa_block: hit.block_name, source: src });
        break;
      }
    }
  }
  return out;
}

/** The source in words: "Teslyn's JSA (revision 2, F26048_…_JSA_R2.pdf)" or "the JSA F26048_…_C210_JSA.pdf". */
export function jsaSourceWords(s: JsaHeightsSource): string {
  return s.kind === 'teslyn_jsa'
    ? `Teslyn's JSA (revision ${s.revision ?? 1}, ${s.file})`
    : `the JSA ${s.file}`;
}

const m = (v: number): string => `${v.toLocaleString('en-AU', { maximumFractionDigits: 2 })} m`;

/** The QC settings' starting clearances: the saved one, else the JSA's (pre-filled, not saved until Save). */
export function qcClearancePrefill(
  blocks: readonly Pick<Block, 'id' | 'kind' | 'terrain_clearance_m'>[],
  fromJsa: readonly JsaClearance[],
): Record<string, number | null> {
  return Object.fromEntries(
    blocks
      .filter((b) => b.kind === 'survey')
      .map((b) => [b.id, b.terrain_clearance_m ?? fromJsa.find((c) => c.block_id === b.id)?.metres ?? null]),
  );
}

/** What the screen says under a block's planned clearance, and whether it offers the JSA's value. */
export interface ClearanceNote {
  readonly text: string;
  /** The JSA's value to offer ("Use 35 m"), when the field holds another. */
  readonly offer: number | null;
}

/**
 * Where the value in a block's planned clearance field came from (R13: show it and let the person change it).
 * `typed` is the field now (null: empty), `saved` the block's stored value.
 */
export function qcClearanceNote(
  typed: number | null,
  saved: number | null,
  jsa: JsaClearance | undefined,
): ClearanceNote | null {
  if (!jsa) return saved === null && typed === null ? null : { text: savedWords(typed, saved), offer: null };
  const from = jsaSourceWords(jsa.source);
  const block = `block ${jsa.jsa_block}`;
  if (typed === jsa.metres) {
    return saved === typed
      ? { text: `Saved; the same as ${from} for ${block}.`, offer: null }
      : { text: `From ${from} for ${block}. Not saved yet: Save QC settings to keep it.`, offer: null };
  }
  return {
    text: `${typed === null ? 'Empty' : savedWords(typed, saved)}; ${from} gives ${m(jsa.metres)} for ${block}.`,
    offer: jsa.metres,
  };
}

function savedWords(typed: number | null, saved: number | null): string {
  if (typed === null) return 'Empty: no terrain clearance check until it is set.';
  return typed === saved ? 'Saved' : 'Typed here, not saved yet';
}

// ------------------------------------------------------------------------------- F11: the heights script

/** A height channel of the aircraft type's heights script and the project's height for it (`ScriptHeight`). */
export interface QcScriptHeight {
  readonly channel: string;
  readonly metres: number;
}

/**
 * The channels the heights scripts make from the DTM. Every type's heights script (`2.1EDIT_QC_HT_Above_DTM_MB.gs`,
 * `C210_Script_2_V2.10_Elevation_QC_MB.gs`, `Heli_Script_2_V1.00_MB.gs`) makes all three in the company templates of
 * 3 Nov 2026; an older C210 or helicopter copy makes the survey height's only, and Teslyn's copy of it for a review adds
 * the other two (decision 874: the three heights alike on every aircraft type).
 *
 * Decision 965: the hard deck's channel is `DTM_plus_Hard_Deck_HT`. Until 3 Nov 2026 the scripts spelt it
 * `DTM_plus_Dard_Deck_HT`; projects keep copies with that name, so both are read as the hard deck
 * (`isHardDeckChannel`), and a script that has the old name takes the hard deck there.
 */
export const QC_HEIGHT_CHANNELS = {
  survey: 'DTM_plus_Survey_HT',
  lowerWorking: 'DTM_LWH_HT',
  hardDeck: 'DTM_plus_Hard_Deck_HT',
} as const;

/** The hard deck channel's name in older copies of the heights scripts (decision 965). */
export const QC_HARD_DECK_CHANNEL_OLD = 'DTM_plus_Dard_Deck_HT';

/** Either name of the hard deck's channel, in any case. */
export function isHardDeckChannel(channel: string): boolean {
  const c = channel.trim().toLowerCase();
  return c === QC_HEIGHT_CHANNELS.hardDeck.toLowerCase() || c === QC_HARD_DECK_CHANNEL_OLD.toLowerCase();
}

/** What a heights channel draws, by either name: null for another channel. */
export function qcHeightOfChannel(channel: string): 'survey' | 'lowerWorking' | 'hardDeck' | null {
  const c = channel.trim().toLowerCase();
  if (c === QC_HEIGHT_CHANNELS.survey.toLowerCase()) return 'survey';
  if (c === QC_HEIGHT_CHANNELS.lowerWorking.toLowerCase()) return 'lowerWorking';
  return isHardDeckChannel(c) ? 'hardDeck' : null;
}

/**
 * MB, 4 Nov 2026 (R196): what Teslyn shows people for the heights channels in its plots, the Height breach report and
 * its words: "Survey height", "Lower working limit" and "Hard Deck" (his capitals). Only the label: the Geosoft
 * channels keep their names in the scripts, views and databases (decision 965).
 */
export const QC_HEIGHT_CHANNEL_LABELS = {
  survey: 'Survey height',
  lowerWorking: 'Lower working limit',
  hardDeck: 'Hard Deck',
} as const;

/**
 * The label Teslyn shows for a channel: the heights channels by MB's names (either hard deck name); any other channel
 * without its edit suffix, `_ED` or `_mED` (MB, 4 Nov 2026: "GpsHt", not "GpsHt_ED"; "RadAlt", not "RadAlt_mED";
 * `GpsHt_ED_D1` is "GpsHt_D1").
 */
export function qcChannelLabel(channel: string): string {
  const kind = qcHeightOfChannel(channel);
  if (kind) return QC_HEIGHT_CHANNEL_LABELS[kind];
  const bare = channel.replace(/_m?ED(?=_|$)/, '');
  return bare === '' ? channel : bare;
}

/** The heights a block's heights script draws, and whether each came from Project settings (decision 967). */
export interface BlockScriptHeights {
  /** The block's survey height (its planned terrain clearance); null: none set, the script keeps its own. */
  readonly survey_m: number | null;
  readonly lower_working_m: number | null;
  readonly hard_deck_m: number | null;
  /** Project settings hold no lower working height: two thirds of the survey height. */
  readonly lower_working_default: boolean;
  /** Project settings hold no hard deck: half the survey height. */
  readonly hard_deck_default: boolean;
}

/** A height Teslyn writes: more than 0 and at most 1,000 m (the QC settings' own limits). */
const usable = (v: number | null | undefined): number | null =>
  v != null && Number.isFinite(v) && v > 0 && v <= 1000 ? v : null;

/** To the nearest metre, a half up (17.5 m is 18 m). */
const toMetre = (v: number): number => Math.floor(v + 0.5);

/**
 * MB's decision 967: the heights written into a block's heights scripts (and given to "Review line"). The survey
 * height is the block's; the lower working height and hard deck are Project settings', and when Project settings hold
 * none, two thirds and half of the survey height, each rounded to the nearest metre, a half up (35 m: 23 m and 18 m).
 * A height with nothing to come from stays null, and the script keeps its own number. The same rule as Teslyn Field's
 * `teslyn_geosoft::template_heights::BlockHeights::from_settings`, tested on the same cases.
 */
export function blockScriptHeights(input: {
  readonly surveyM: number | null | undefined;
  readonly lowerWorkingM: number | null | undefined;
  readonly hardDeckM: number | null | undefined;
}): BlockScriptHeights {
  const survey = usable(input.surveyM);
  const lwh = usable(input.lowerWorkingM);
  const deck = usable(input.hardDeckM);
  const positive = (v: number | null): number | null => (v !== null && v > 0 ? v : null);
  const lwhDefault = positive(survey === null ? null : toMetre((survey * 2) / 3));
  const deckDefault = positive(survey === null ? null : toMetre(survey / 2));
  return {
    survey_m: survey,
    lower_working_m: lwh ?? lwhDefault,
    hard_deck_m: deck ?? deckDefault,
    lower_working_default: lwh === null && lwhDefault !== null,
    hard_deck_default: deck === null && deckDefault !== null,
  };
}

/**
 * The survey height of a block folder's heights scripts: the block whose key the folder ends with (`F26048_Milgun_patch`
 * for `Milgun_patch`; the longest key when several match), its planned terrain clearance; for a folder of no block, or a
 * block with none, the project's when every survey block that has one has the same. The same rule as Teslyn Field's
 * `geosoft_blocks::block_survey_height`.
 */
export function blockFolderSurveyHeight(
  folder: string,
  blocks: readonly Pick<Block, 'key' | 'kind' | 'terrain_clearance_m'>[],
): number | null {
  const f = folder.toLowerCase();
  const own = blocks
    .filter((b) => {
      const k = b.key.toLowerCase();
      return k !== '' && (f === k || f.endsWith(`_${k}`));
    })
    .sort((a, b) => b.key.length - a.key.length)[0];
  const ownM = usable(own?.terrain_clearance_m);
  if (ownM !== null) return ownM;
  const all = new Set(
    blocks
      .filter((b) => b.kind === 'survey')
      .flatMap((b) => {
        const v = usable(b.terrain_clearance_m);
        return v === null ? [] : [v];
      }),
  );
  return all.size === 1 ? [...all][0]! : null;
}

/**
 * The heights in words, for Project settings and the checklist: "survey height 35 m, lower working height 23 m (two
 * thirds of the survey height, as none is set), hard deck 18 m (…)". Empty when there is none.
 */
export function blockScriptHeightWords(h: BlockScriptHeights): string {
  const parts = [
    h.survey_m !== null ? `survey height ${m(h.survey_m)}` : '',
    h.lower_working_m !== null
      ? `lower working height ${m(h.lower_working_m)}${h.lower_working_default ? ' (two thirds of the survey height, as none is set)' : ''}`
      : '',
    h.hard_deck_m !== null
      ? `hard deck ${m(h.hard_deck_m)}${h.hard_deck_default ? ' (half the survey height, as none is set)' : ''}`
      : '',
  ].filter(Boolean);
  return parts.join(', ');
}

/**
 * What each survey block's Geosoft heights script draws, for Project settings, QC limits and Flight data QC's QC
 * settings: "Each block's Geosoft heights script draws: Milgun: survey height 35 m, …; Milgun Patch: …." (one line when
 * every block draws the same: "Every block's Geosoft heights script draws survey height 35 m, …."). Empty when no
 * block has a height to draw.
 */
export function blocksScriptHeightsWords(
  project: { readonly qc_lower_working_m?: number | null; readonly qc_hard_deck_m?: number | null },
  blocks: readonly Pick<Block, 'name' | 'kind' | 'terrain_clearance_m'>[],
): string {
  const each = blocks
    .filter((b) => b.kind === 'survey')
    .map((b) => ({
      name: b.name,
      words: blockScriptHeightWords(
        blockScriptHeights({
          surveyM: b.terrain_clearance_m,
          lowerWorkingM: project.qc_lower_working_m,
          hardDeckM: project.qc_hard_deck_m,
        }),
      ),
    }))
    .filter((x) => x.words !== '');
  if (each.length === 0) return '';
  if (each.length > 1 && each.every((x) => x.words === each[0]!.words))
    return `Every block's Geosoft heights script draws ${each[0]!.words}.`;
  return `Each block's Geosoft heights script draws: ${each.map((x) => `${x.name}: ${x.words}`).join('; ')}.`;
}

const blockDigitOf = (lineName: string): string => lineName.replace(/^[A-Z]+/, '').charAt(0);

/**
 * MB's F11: the heights a line's review gives the heights script: the survey height of the line's block (its planned
 * terrain clearance, Project settings, QC limits), the crew's lower working height and hard deck, and when Project
 * settings hold none of those two, decision 967's defaults (`blockScriptHeights`), as the block's own copy of the script
 * has. A height with nothing to come from is left out, so the script keeps its own.
 */
export function qcScriptHeights(input: {
  readonly lineName: string;
  readonly blocks: readonly Pick<Block, 'digit' | 'kind' | 'terrain_clearance_m'>[];
  readonly hardDeckM: number | null;
  readonly lowerWorkingM: number | null;
}): QcScriptHeight[] {
  const block = input.blocks.find((b) => b.kind === 'survey' && b.digit === blockDigitOf(input.lineName));
  const out: QcScriptHeight[] = [];
  const add = (channel: string, v: number | null | undefined): void => {
    if (v != null && v > 0) out.push({ channel, metres: v });
  };
  const h = blockScriptHeights({
    surveyM: block?.terrain_clearance_m,
    lowerWorkingM: input.lowerWorkingM,
    hardDeckM: input.hardDeckM,
  });
  add(QC_HEIGHT_CHANNELS.survey, h.survey_m);
  add(QC_HEIGHT_CHANNELS.lowerWorking, h.lower_working_m);
  add(QC_HEIGHT_CHANNELS.hardDeck, h.hard_deck_m);
  return out;
}

/**
 * The heights a review job used, in words for the message ("survey height 40 m (the script has 35 m)"; a channel
 * Teslyn's copy added to the script, `script_m` null: "lower working height 25 m (added to the script)").
 */
export function qcScriptHeightWords(
  changed: readonly { readonly channel: string; readonly script_m: number | null; readonly metres: number }[],
): string {
  const what = {
    survey: 'survey height',
    lowerWorking: 'lower working height',
    hardDeck: 'hard deck',
  } as const;
  if (changed.length === 0) return '';
  const parts = changed.map((c) => {
    const kind = qcHeightOfChannel(c.channel);
    const name = kind ? what[kind] : c.channel;
    if (c.script_m == null) return `${name} ${m(c.metres)} (added to the script)`;
    return c.script_m === c.metres
      ? `${name} ${m(c.metres)}`
      : `${name} ${m(c.metres)} (the script has ${m(c.script_m)})`;
  });
  return `The heights script uses the project's ${parts.join(', ')}.`;
}
